import type { ProcessDiagramPositionType, ProcessTemplateTaskType } from '@econtractor/types';
import { getProcessNextTasks, processTaskInstanceKey } from './nextTask';

const COLUMN_STEP_X = 408;
const SIBLING_GAP = 28;
const ROOT_TREE_GAP = 40;
const BASE_NODE_HEIGHT = 96;
const EXTRA_ROW_HEIGHT = 28;

const estimateNodeHeight = (task: ProcessTemplateTaskType): number => {
    const answers = task.answers?.length ?? 0;
    const outgoing = getProcessNextTasks(task.processData).length;
    return BASE_NODE_HEIGHT + answers * EXTRA_ROW_HEIGHT + outgoing * EXTRA_ROW_HEIGHT;
};

export const layoutProcessDiagramTasks = (
    tasks: ProcessTemplateTaskType[],
): Map<string, ProcessDiagramPositionType> => {
    const taskByKey = new Map(tasks.map((task) => [processTaskInstanceKey(task.templateId, task.dtId), task]));
    const indexByKey = new Map(
        tasks.map((task, index) => [processTaskInstanceKey(task.templateId, task.dtId), index]),
    );
    const childrenByKey = new Map<string, string[]>();
    const incomingCount = new Map<string, number>();

    for (const task of tasks) {
        const taskKey = processTaskInstanceKey(task.templateId, task.dtId);
        incomingCount.set(taskKey, incomingCount.get(taskKey) ?? 0);
        for (const link of getProcessNextTasks(task.processData)) {
            const targetKey = processTaskInstanceKey(link.templateId, link.dtId);
            if (!taskByKey.has(targetKey)) continue;
            const children = childrenByKey.get(taskKey) || [];
            if (!children.includes(targetKey)) children.push(targetKey);
            childrenByKey.set(taskKey, children);
            incomingCount.set(targetKey, (incomingCount.get(targetKey) ?? 0) + 1);
        }
    }

    const sortByInitialOrder = (a: string, b: string) => (indexByKey.get(a) ?? 0) - (indexByKey.get(b) ?? 0);
    childrenByKey.forEach((keys, key) => {
        childrenByKey.set(key, [...keys].sort(sortByInitialOrder));
    });

    const roots = tasks
        .filter((task) => (incomingCount.get(processTaskInstanceKey(task.templateId, task.dtId)) ?? 0) === 0)
        .map((task) => processTaskInstanceKey(task.templateId, task.dtId))
        .sort(sortByInitialOrder);

    const used = new Set<string>();
    const map = new Map<string, ProcessDiagramPositionType>();
    let nextY = 0;

    const walk = (taskKey: string, depth: number) => {
        if (used.has(taskKey)) return;
        used.add(taskKey);
        map.set(taskKey, { x: depth * COLUMN_STEP_X, y: nextY });
        const task = taskByKey.get(taskKey);
        nextY += (task ? estimateNodeHeight(task) : BASE_NODE_HEIGHT) + SIBLING_GAP;
        for (const childKey of childrenByKey.get(taskKey) || []) walk(childKey, depth + 1);
    };

    for (const key of roots) {
        walk(key, 0);
        nextY += ROOT_TREE_GAP;
    }
    for (const task of tasks) {
        const taskKey = processTaskInstanceKey(task.templateId, task.dtId);
        if (used.has(taskKey)) continue;
        walk(taskKey, 0);
        nextY += ROOT_TREE_GAP;
    }
    return map;
};
